import { useState, useEffect, createContext, useContext } from 'react';

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Theme Provider Component
 */
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('admin-theme');
    if (savedTheme) {
      return savedTheme;
    }
    
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    return 'light';
  });

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Save to localStorage
    localStorage.setItem('admin-theme', theme);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('admin-theme');
      if (!savedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const setLightTheme = () => setTheme('light');
  const setDarkTheme = () => setTheme('dark');

  const value = {
    theme,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Theme Toggle Button Component
 */
export const ThemeToggle = ({ className = '' }) => {
  const { theme, toggleTheme, isDark } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`relative inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors duration-200 ${className}`}
      title={isDark ? 'מעבר למצב יום' : 'מעבר למצב לילה'}
    >
      {/* Sun Icon */}
      <svg
        className={`w-5 h-5 text-yellow-500 transition-all duration-300 ${
          isDark ? 'opacity-0 rotate-180 scale-0' : 'opacity-100 rotate-0 scale-100'
        }`}
        fill="currentColor"
        viewBox="0 0 20 20"
        style={{ position: isDark ? 'absolute' : 'static' }}
      >
        <path
          fillRule="evenodd"
          d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
          clipRule="evenodd"
        />
      </svg>

      {/* Moon Icon */}
      <svg
        className={`w-5 h-5 text-gray-200 transition-all duration-300 ${
          isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-180 scale-0'
        }`}
        fill="currentColor"
        viewBox="0 0 20 20"
        style={{ position: isDark ? 'static' : 'absolute' }}
      >
        <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
      </svg>
    </button>
  );
};

/**
 * Theme-aware color utilities
 */
export const useThemeColors = () => {
  const { isDark } = useTheme();

  const colors = {
    // Backgrounds
    bg: {
      primary: isDark ? 'bg-gray-900' : 'bg-white',
      secondary: isDark ? 'bg-gray-800' : 'bg-gray-50',
      tertiary: isDark ? 'bg-gray-700' : 'bg-gray-100'
    },
    
    // Text colors
    text: {
      primary: isDark ? 'text-white' : 'text-gray-900',
      secondary: isDark ? 'text-gray-300' : 'text-gray-600',
      tertiary: isDark ? 'text-gray-400' : 'text-gray-500'
    },
    
    // Borders
    border: {
      primary: isDark ? 'border-gray-700' : 'border-gray-200',
      secondary: isDark ? 'border-gray-600' : 'border-gray-300'
    },
    
    // Interactive elements
    interactive: {
      hover: isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50',
      active: isDark ? 'bg-gray-700' : 'bg-gray-100'
    }
  };

  return colors;
};

/**
 * Enhanced CSS classes for dark mode support
 */
export const getThemeClasses = (baseClasses) => {
  return {
    ...baseClasses,
    // Card styles
    card: `${baseClasses.card || ''} dark:bg-gray-800 dark:border-gray-700`,
    
    // Input styles  
    input: `${baseClasses.input || ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400`,
    
    // Button styles
    button: {
      primary: `${baseClasses.button?.primary || ''} dark:bg-primary-600 dark:hover:bg-primary-700`,
      secondary: `${baseClasses.button?.secondary || ''} dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white`,
      outline: `${baseClasses.button?.outline || ''} dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700`
    },
    
    // Text styles
    text: {
      heading: `${baseClasses.text?.heading || ''} dark:text-white`,
      body: `${baseClasses.text?.body || ''} dark:text-gray-300`,
      muted: `${baseClasses.text?.muted || ''} dark:text-gray-400`
    }
  };
};

/**
 * Custom hook for theme-aware animations
 */
export const useThemeTransitions = () => {
  const { theme } = useTheme();
  
  return {
    // Smooth theme transitions
    transition: 'transition-colors duration-200 ease-in-out',
    
    // Theme change animation
    themeChange: {
      from: theme === 'dark' ? 'opacity-0 scale-95' : 'opacity-100 scale-100',
      to: theme === 'dark' ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
    },
    
    // Fade effects
    fadeIn: 'animate-fade-in',
    fadeOut: 'animate-fade-out'
  };
};
